/**
 * Task 11 — Super_admin guarantor SSN reveal modal.
 *
 * Flow:
 *  1. Eye icon next to the masked SSN on AdminCustomerDetailPage (super_admin only).
 *  2. Click → opens this modal with password + reason fields.
 *  3. Submit → POST /api/auth/refresh (mints a fresh token so the
 *     ``iat`` claim is < 5min, satisfying ``_check_fresh_auth``) →
 *     POST /api/admin/customers/{id}/guarantor-ssn/reveal.
 *  4. Display full SSN + 30s countdown → auto-mask.
 *  5. Audit row ``ssn_revealed`` is written server-side with the reason.
 */
import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Eye, EyeOff, Copy, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REVEAL_DURATION_SEC = 30;

export default function SSNRevealModal({ open, onOpenChange, customerId, customerName }) {
    const { user } = useAuth();
    const [password, setPassword] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [revealed, setRevealed] = useState(null);  // { ssn, expiresAt }
    const [remainingSec, setRemainingSec] = useState(REVEAL_DURATION_SEC);

    // Countdown timer once revealed.
    useEffect(() => {
        if (!revealed) return;
        const tick = () => {
            const left = Math.max(0, Math.ceil((revealed.expiresAt - Date.now()) / 1000));
            setRemainingSec(left);
            if (left <= 0) setRevealed(null);
        };
        tick();
        const id = setInterval(tick, 250);
        return () => clearInterval(id);
    }, [revealed]);

    // Reset state when modal closes.
    useEffect(() => {
        if (!open) {
            setPassword(""); setReason(""); setRevealed(null);
            setRemainingSec(REVEAL_DURATION_SEC);
        }
    }, [open]);

    const reasonOk = reason.trim().length >= 8;
    const canSubmit = password && reasonOk && !busy;

    async function submit() {
        if (!canSubmit) return;
        setBusy(true);
        try {
            // Step 1 — re-verify password by minting a fresh access
            // token via /auth/login. This both proves the user knows
            // the current password AND stamps a fresh ``iat`` claim so
            // the server's _check_fresh_auth gate (5-min window) passes.
            try {
                const { data: login } = await api.post("/auth/login", {
                    email: user?.email,
                    password,
                });
                localStorage.setItem("rp_token", login.token);
            } catch (loginErr) {
                toast.error("Password incorrect");
                setBusy(false);
                return;
            }
            // Step 2 — actual reveal with the fresh token.
            const { data } = await api.post(
                `/admin/customers/${customerId}/guarantor-ssn/reveal`,
                { reason: reason.trim() },
            );
            setRevealed({
                ssn: data.ssn,
                expiresAt: Date.now() + REVEAL_DURATION_SEC * 1000,
            });
            setRemainingSec(REVEAL_DURATION_SEC);
            toast.success("SSN revealed (auto-mask in 30s)");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    async function copyToClipboard() {
        if (!revealed) return;
        try {
            await navigator.clipboard.writeText(revealed.ssn);
            toast.success("Copied");
        } catch {
            toast.error("Copy failed");
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="ssn-reveal-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Lock size={16} className="text-red-600" />
                        Reveal guarantor SSN
                    </DialogTitle>
                    <DialogDescription>
                        {customerName
                            ? <>Decrypts the SSN for <span className="font-medium">{customerName}</span>. This is logged with your reason.</>
                            : "Decrypts the guarantor SSN. This is logged with your reason."}
                    </DialogDescription>
                </DialogHeader>

                {!revealed ? (
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="ssn-reveal-pwd">Your password</Label>
                            <Input
                                id="ssn-reveal-pwd"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Re-enter your password"
                                data-testid="ssn-reveal-password"
                                autoFocus
                            />
                            <p className="text-[11px] text-slate-500">
                                Required to mint a fresh access token for this sensitive action.
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ssn-reveal-reason">Reason (min 8 chars)</Label>
                            <Textarea
                                id="ssn-reveal-reason"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Tax-filing review for ALL-12345, requested by accounting on…"
                                data-testid="ssn-reveal-reason"
                            />
                            <p className="text-[11px] text-slate-500">
                                Stored in the audit log alongside your user id and timestamp.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-center justify-between">
                            <span className="font-mono text-lg tracking-widest text-amber-900"
                                  data-testid="ssn-reveal-value">
                                {revealed.ssn}
                            </span>
                            <Button
                                variant="ghost" size="sm" onClick={copyToClipboard}
                                data-testid="ssn-reveal-copy"
                            >
                                <Copy size={14} className="mr-1" /> Copy
                            </Button>
                        </div>
                        <p className="text-xs text-slate-600 flex items-center gap-1.5"
                           data-testid="ssn-reveal-countdown">
                            <EyeOff size={13} /> Auto-masks in {remainingSec}s
                        </p>
                    </div>
                )}

                <DialogFooter>
                    {!revealed ? (
                        <>
                            <Button variant="ghost" onClick={() => onOpenChange(false)}
                                data-testid="ssn-reveal-cancel">Cancel</Button>
                            <Button
                                onClick={submit} disabled={!canSubmit}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="ssn-reveal-submit"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Eye size={14} className="mr-1" />}
                                Reveal
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => onOpenChange(false)} data-testid="ssn-reveal-close">
                            Close
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
